/**
 * 생성기 입력창 화면 상태 — 빌드 화면의 다른 전역 원자와 같은 jotai 원자. 실행 상태는 aiGenerateDockRun.atoms.
 */
import { atom } from 'jotai';
import { atomWithReducer } from 'jotai/utils';

import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { type AiGeneratorDockPlace } from '../utils/events.quiz-ai-generator';

import { aiGenerateDockRunningCountAtom } from './aiGenerateDockRun.atoms';

type AiGenerateDockUiState = {
  /** 입력창이 열렸나 — 열려 있어도 생성 중엔 보이지 않는다(aiGenerateDockInputShownAtom). */
  open: boolean;
  /** 자료 탭 — 상단 QUIZ AI 카드·입력창 탭이 함께 쓴다. 입력창을 닫아도 남는다. */
  tab: AiGenerateDockSourceType;
  /**
   * 사용자가 닫혀 있던 입력창을 연 표면 — 입력창이 열림을 확인하고 계측한 뒤 비운다.
   * 실행 결과로 다시 여는 경우(place 없음)는 사용자 행동이 아니라 남기지 않는다.
   */
  pendingOpenPlace: AiGeneratorDockPlace | null;
};

const INITIAL_AI_GENERATE_DOCK_UI: AiGenerateDockUiState = {
  open: false,
  tab: 'query',
  pendingOpenPlace: null,
};

export type AiGenerateDockUiAction =
  /**
   * 입력창을 그 탭으로 연다 — 열린 채로 카드의 다른 선택지를 눌러도 탭만 옮긴다(쓰던 주제는 남는다).
   * 진입 카드는 연 표면(place)을 함께 넘긴다. 닫혀 있었을 때만 남긴다(열린 채면 탭 이동).
   */
  | {
      type: 'open';
      tab: AiGenerateDockSourceType;
      place?: AiGeneratorDockPlace;
    }
  /** 입력창(✕·Esc·실행)이 닫는다. */
  | { type: 'close' }
  | { type: 'selectTab'; tab: AiGenerateDockSourceType }
  | { type: 'consumeOpenPlace' }
  | { type: 'reset' };

export const reduceAiGenerateDockUi = (
  state: AiGenerateDockUiState,
  action: AiGenerateDockUiAction,
): AiGenerateDockUiState => {
  switch (action.type) {
    case 'open':
      return {
        open: true,
        tab: action.tab,
        pendingOpenPlace:
          action.place && !state.open ? action.place : state.pendingOpenPlace,
      };
    case 'close':
      return { ...state, open: false };
    case 'selectTab':
      return { ...state, tab: action.tab };
    case 'consumeOpenPlace':
      return { ...state, pendingOpenPlace: null };
    case 'reset':
      return INITIAL_AI_GENERATE_DOCK_UI;
  }
};

/** 생성기 입력창 화면 상태 — 열림·탭·연 표면의 전이는 reduceAiGenerateDockUi 한 곳이다. */
export const aiGenerateDockUiAtom = atomWithReducer(
  INITIAL_AI_GENERATE_DOCK_UI,
  reduceAiGenerateDockUi,
);

export const aiGenerateDockOpenAtom = atom(
  get => get(aiGenerateDockUiAtom).open,
);

export const aiGenerateDockTabAtom = atom(get => get(aiGenerateDockUiAtom).tab);

export const aiGenerateDockOpenPlaceAtom = atom(
  get => get(aiGenerateDockUiAtom).pendingOpenPlace,
);

/**
 * 입력창이 실제로 보이나 — 열려 있어도 생성 중엔 보이지 않는다(PRD 동작 규칙: 생성 중엔 입력창을 다시 열 수 없다).
 * 생성 중 진행은 생성 중 토스트가, 20문제 가득은 열기 요청(useRequestAiGenerateDock)의 상한 토스트가 보인다.
 */
export const aiGenerateDockInputShownAtom = atom(
  get =>
    get(aiGenerateDockOpenAtom) && get(aiGenerateDockRunningCountAtom) === null,
);
